import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { gqlClient } from '@/api/graphql-client';
import { getNextPageParamFromPaginatedResult } from '@/api/infiniteQueries';
import { graphql } from '@/gql';
import { GetSuppliersInput, PaginationInput } from '@/gql/graphql';
import { PAGINATION_ARG_DEFAULTS } from '@payaca/shared-isomorphic/constants/graphql-pagination';
import { useEffect } from 'react';
import supplierKeys from './keyFactory';

const GetSuppliers = graphql(`
  query Suppliers($pagination: PaginationInput!, $input: GetSuppliersInput!) {
    suppliers(input: $input, pagination: $pagination) {
      items {
        id
        name
        email
        phone
        website
        accountConnectionMetadata
        addresses {
          address {
            line1
            line2
            city
            postcode
          }
        }
      }
      offset
      limit
      totalCount
    }
  }
`);

const useGetSuppliers = (
  input: GetSuppliersInput,
  pagination?: PaginationInput,
  enabled: boolean = true
) => {
  const { data, ...rest } = useQuery({
    queryKey: supplierKeys.search(input, pagination || PAGINATION_ARG_DEFAULTS),
    queryFn: () => {
      return gqlClient.request(GetSuppliers, {
        input,
        pagination: pagination || PAGINATION_ARG_DEFAULTS,
      });
    },
    enabled,
  });

  return { suppliers: data?.suppliers, data, ...rest };
};

const useInfiniteGetSuppliers = (input: GetSuppliersInput, limit?: number) => {
  const queryFn = ({ pageParam }: { pageParam: number }) => {
    return gqlClient.request(GetSuppliers, {
      input,
      pagination: {
        offset: pageParam,
        limit: limit || PAGINATION_ARG_DEFAULTS.limit,
      },
    });
  };

  return useInfiniteQuery({
    queryKey: supplierKeys.infiniteSuppliers(input),
    queryFn,
    initialPageParam: PAGINATION_ARG_DEFAULTS.offset,
    getNextPageParam: getNextPageParamFromPaginatedResult(
      (lastPage) => lastPage.suppliers
    ),
  });
};

export const useGetAllPaginatedSuppliers = (
  input: GetSuppliersInput,
  limit?: number
) => {
  const { data, hasNextPage, fetchNextPage, fetchStatus, status, isLoading } =
    useInfiniteGetSuppliers(input, limit);

  const suppliers = data?.pages.flatMap((page) => page.suppliers.items) || [];

  useEffect(() => {
    if (status === 'success' && fetchStatus === 'idle' && hasNextPage) {
      // Fetch the next page when the previous fetch has completed successfully
      void fetchNextPage();
    }
  }, [status, fetchStatus, hasNextPage, fetchNextPage]);

  return { suppliers, isLoading };
};

export default useGetSuppliers;
